import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNewAppointments } from '../../hooks/useNewAppointments';
import {
  Home, Users, PawPrint, Calendar, Stethoscope,
  Syringe, CreditCard, FileText, Building2, BarChart3, X,
} from 'lucide-react';

const menuItems = [
  { path: '/app', icon: Home, label: 'Kontrol Paneli' },
  { path: '/app/owners', icon: Users, label: 'Hayvan Sahipleri' },
  { path: '/app/pets', icon: PawPrint, label: 'Hastalar' },
  { path: '/app/appointments', icon: Calendar, label: 'Randevular' },
  { path: '/app/examinations', icon: Stethoscope, label: 'Muayeneler' },
  { path: '/app/vaccinations', icon: Syringe, label: 'Aşılar' },
  { path: '/app/vaccine-card', icon: CreditCard, label: 'Aşı Kartı' },
  { path: '/app/patient-report', icon: FileText, label: 'Hasta Raporu' },
  { path: '/app/statistics', icon: BarChart3, label: 'İstatistikler' },
  { path: '/app/clinic-settings', icon: Building2, label: 'Klinik Ayarları' },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, '') || '/';
  const { unreadCount } = useNewAppointments();

  return (
    <div
      className={`
        fixed top-16 bottom-0 w-60
        z-[120] lg:z-[10]
        lg:w-52 lg:bottom-3 lg:left-3 lg:top-[4.5rem]
        ${isOpen ? 'left-0' : '-left-64'}
        transition-[left] duration-300 ease-in-out
        lg:left-3
      `}
    >
      <div
        className="flex h-full min-h-0 max-h-full flex-col overflow-hidden max-lg:max-h-[calc(100vh-4rem)] rounded-2xl"
        style={{ backgroundColor: '#2F4420' }}
      >
        {/* Logo */}
        <div className="flex shrink-0 items-center gap-2.5 px-4 py-3.5" style={{ borderBottom: '1px solid rgba(200,218,214,0.12)' }}>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: '#557236' }}>
            <img src="/brand-symbol.png" alt="" className="h-5 w-auto" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-[14px] font-bold leading-tight tracking-tight text-white">KlinikVet</h1>
            <p className="text-[10px] font-medium mt-0.5" style={{ color: '#A9B98A' }}>Klinik Paneli</p>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-lg transition-colors"
            style={{ color: '#A9B98A' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Section label */}
        <div className="shrink-0 px-4 pt-4 pb-1.5">
          <p className="text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: '#7A8F5C' }}>Modüller</p>
        </div>

        {/* Nav */}
        <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          <ul className="space-y-0.5">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const normalizedPath = item.path.replace(/\/$/, '');
              const isActive =
                normalizedPath === '/app'
                  ? pathname === '/app'
                  : pathname === normalizedPath || pathname.startsWith(`${normalizedPath}/`);

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={onClose}
                    className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-[13px] font-medium leading-tight transition-all"
                    style={isActive
                      ? { backgroundColor: 'rgba(61,139,125,0.18)', color: '#D2DEB4' }
                      : { color: '#A9B98A' }
                    }
                    onMouseEnter={e => {
                      if (!isActive) (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.05)';
                    }}
                    onMouseLeave={e => {
                      if (!isActive) (e.currentTarget as HTMLElement).style.backgroundColor = '';
                    }}
                  >
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg relative transition-colors"
                      style={isActive
                        ? { backgroundColor: 'rgba(61,139,125,0.25)', color: '#D2DEB4' }
                        : { backgroundColor: 'rgba(255,255,255,0.06)', color: '#A9B98A' }
                      }
                    >
                      <Icon className="h-3.5 w-3.5" strokeWidth={isActive ? 2.5 : 2} />
                      {item.path === '/app/appointments' && unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full bg-rose-500 text-white text-[8px] font-bold" style={{ boxShadow: '0 0 0 2px #2F4420' }}>
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </span>
                    <span className="min-w-0 leading-tight flex-1">{item.label}</span>
                    {item.path === '/app/appointments' && unreadCount > 0 && (
                      <span className="text-[9px] font-bold text-rose-400">YENİ</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom */}
        <div className="shrink-0 px-3 py-3" style={{ borderTop: '1px solid rgba(200,218,214,0.12)' }}>
          <Link
            to="/demo-request"
            onClick={onClose}
            className="block rounded-xl px-3 py-2 text-center text-[11px] font-medium transition-colors"
            style={{ border: '1px solid rgba(61,139,125,0.3)', backgroundColor: 'rgba(61,139,125,0.1)', color: '#D2DEB4' }}
          >
            Destek veya teklif →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
